import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { logErr } from "../lib/utils-trel";
import { Shield, Lock, User, Mail, Phone, Calendar, Clock } from "lucide-react";

const Field = ({ label, ...rest }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-[#064E3B]/70">{label}</label>
    <input className="kbd-input" {...rest}/>
  </div>
);

const Card = ({ icon:I, title, children }) => (
  <div className="rounded-3xl card-luxury p-7">
    <div className="flex items-center gap-2 mb-4">
      {I && <I size={18} className="text-[#064E3B]"/>}
      <h2 className="font-serif text-2xl">{title}</h2>
    </div>
    {children}
  </div>
);

export default function AccountSettings(){
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState({ name:"", email:"", phone:"", profile_photo:"" });
  const [pw, setPw] = useState({ current_password:"", new_password:"", confirm:"" });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(()=>{
    if (user) setProfile({ name: user.name||"", email: user.email||"", phone: user.phone||"", profile_photo: user.profile_photo||"" });
  },[user]);

  const saveProfile = async () => {
    setMsg(""); setErr("");
    try {
      await api.patch("/auth/profile", profile);
      await refresh();
      setMsg("Profile updated.");
    } catch (e) {
      setErr(e.response?.data?.detail || "Update failed");
      logErr("profile update", e);
    }
  };

  const changePw = async () => {
    setMsg(""); setErr("");
    if (pw.new_password !== pw.confirm) { setErr("New passwords do not match"); return; }
    if (pw.new_password.length < 8) { setErr("Password must be at least 8 characters"); return; }
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      setPw({ current_password:"", new_password:"", confirm:"" });
      setMsg("Password changed. Stay signed in or log in again.");
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not change password");
      logErr("change password", e);
    }
  };

  if (!user) return <div className="p-20 text-center">Loading…</div>;
  return (
    <div className="max-w-4xl mx-auto px-6 py-10 pb-28 lg:pb-10">
      <h1 className="font-serif text-4xl">Account &amp; Security</h1>
      <p className="text-[#071A2D]/70 mt-1">Manage your profile, password, and account details.</p>

      {/* Identity card */}
      <div className="mt-8 grid md:grid-cols-2 gap-4">
        <Card icon={User} title="Identity">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div><div className="text-xs uppercase tracking-widest text-[#064E3B]/70">Role</div><div className="font-medium capitalize mt-1">{user.role}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-[#064E3B]/70">Status</div><div className="font-medium mt-1">{user.account_status||"active"}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-[#064E3B]/70 flex items-center gap-1"><Calendar size={11}/> Joined</div><div className="font-medium mt-1">{user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-[#064E3B]/70 flex items-center gap-1"><Clock size={11}/> Last login</div><div className="font-medium mt-1">{user.last_login ? new Date(user.last_login).toLocaleString() : "First session"}</div></div>
          </div>
        </Card>

        <Card icon={Mail} title="Profile">
          <div className="space-y-4">
            <Field label="Name" data-testid="acct-name" value={profile.name} onChange={e=>setProfile({...profile, name:e.target.value})}/>
            <Field label="Email" data-testid="acct-email" value={profile.email} onChange={e=>setProfile({...profile, email:e.target.value})}/>
            <Field label="Phone" data-testid="acct-phone" value={profile.phone} onChange={e=>setProfile({...profile, phone:e.target.value})}/>
            <Field label="Profile photo URL" data-testid="acct-photo" value={profile.profile_photo} onChange={e=>setProfile({...profile, profile_photo:e.target.value})}/>
          </div>
          <button onClick={saveProfile} data-testid="acct-save-profile" className="btn-coral mt-5">Save profile</button>
        </Card>
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-4">
        <Card icon={Lock} title="Change password">
          <div className="space-y-4">
            <Field label="Current password" type="password" data-testid="acct-current-pw" value={pw.current_password} onChange={e=>setPw({...pw, current_password:e.target.value})}/>
            <Field label="New password" type="password" data-testid="acct-new-pw" value={pw.new_password} onChange={e=>setPw({...pw, new_password:e.target.value})}/>
            <Field label="Confirm new password" type="password" data-testid="acct-confirm-pw" value={pw.confirm} onChange={e=>setPw({...pw, confirm:e.target.value})}/>
          </div>
          <button onClick={changePw} data-testid="acct-save-pw" className="btn-coral mt-5">Update password</button>
          <div className="text-xs text-[#071A2D]/60 mt-3">Minimum 8 characters · mixed case or a number recommended.</div>
        </Card>

        <Card icon={Shield} title="Sign out everywhere">
          <p className="text-sm text-[#071A2D]/70">Sign out of this device and clear the session.</p>
          <button onClick={async ()=>{ nav("/", { replace: true }); await logout(); }} data-testid="acct-logout" className="btn-outline-emerald mt-5">Sign out</button>
        </Card>
      </div>

      {msg && <div data-testid="acct-msg" className="mt-6 px-4 py-3 rounded-2xl bg-[#064E3B] text-[#FFF8EE] text-sm">{msg}</div>}
      {err && <div data-testid="acct-err" className="mt-6 px-4 py-3 rounded-2xl bg-[#FF7A45]/15 text-[#FF7A45] text-sm">{err}</div>}
    </div>
  );
}
